import { useCallback, useMemo, useState } from 'react'

import { useTrackProductInteraction } from '../../../../../app/_providers/TrackingProvider.client'
import { useTranslation } from '../../../../../app/_providers/TranslationProvider.client'
import { CartContainer } from '../../../../../brunswick/containers/cart'
import { useSelectVariant } from '../../../../../lib/hooks/useSelectVariant'
import { useProductHref } from '../../../../../lib/routing'
import { deriveAvailableProducts } from '../../../../../lib/utils'
import { bulkCartAttributeKey } from '../../constants'

import type { Props, SubmitButtonStatus, ViewModel } from './types'

export function useViewModel({ isPlain, product }: Props): ViewModel {
  const { cart, addGlobalAttributes } = CartContainer.useContainer()
  const [showMobileContents, setShowMobileContents] = useState<boolean>(false)
  const [quantity, setQuantity] = useState<number | undefined>(1)
  const [status, setStatus] = useState<SubmitButtonStatus>('idle')

  const SUCCESS_DURATION = 1500

  const {
    selectAndAddVariant: {
      addToBagButton: { successCta, inFlightAddToBagCta },
    },
  } = useTranslation()

  const { trackProductInteraction } = useTrackProductInteraction()

  // NOTE: The variants pulled from `product.variants` currently do not include extended size variants (FIGS-4449).
  // A subsequent GET_FEATURED_PRODUCT query is called that populates the variants correctly.

  const briefProductDescription = product.productGroup.briefProductDescription ?? undefined
  const coloredAndFitVariants = product.variants ?? []

  const toggleMobileContents = useCallback(() => {
    setShowMobileContents(prev => !prev)
  }, [setShowMobileContents])

  const incrementQuantity = useCallback(() => {
    if (quantity !== undefined) {
      const newQuantity = quantity + 1
      setQuantity(newQuantity)
    }
  }, [quantity])

  const decrementQuantity = useCallback(() => {
    if (quantity !== undefined) {
      const newQuantity = Math.max(1, quantity - 1)
      setQuantity(newQuantity)
    }
  }, [quantity])

  const onQuantityChange = useCallback((value: number | undefined) => {
    setQuantity(value)
  }, [])

  const availableProducts = useMemo(() => {
    return deriveAvailableProducts(product.productGroup.productsForAvailability)
  }, [product.productGroup.productsForAvailability])

  const selectVariantOutput = useSelectVariant({
    availableProducts,
    coloredAndFitVariants,
    imageSize: 350,
    initialFeaturedProduct: product,
    initialSelectedColor: product.colorInfo,
    productCategory: product.productGroup.category,
    productGroup: product.productGroup,
    openMiniCartOnAdd: false,
    quantity,
  })

  const {
    productGroup: { handle: productGroupHandle },
  } = product

  const pdpHref = useProductHref(productGroupHandle, {
    color: selectVariantOutput.selectedColor?.rawName,
    plain: isPlain || undefined,
  })

  const {
    selectAndAddVariant: {
      addToBagButton: { viewProduct },
    },
  } = useTranslation()

  const onProductView = () => {
    trackProductInteraction({
      handle: product.handle || '',
      shopifyId: product.shopifyId,
      id: product.id,
    })
  }

  const { isValidToAdd } = selectVariantOutput

  const handleAddToCart = useCallback(() => {
    const hasBulkAttribute = cart.attributes.some(
      attribute => attribute.key === bulkCartAttributeKey
    )
    if (!hasBulkAttribute) addGlobalAttributes([{ key: bulkCartAttributeKey, value: 'true' }])

    selectVariantOutput.ctaButton.onClick()

    if (isValidToAdd) {
      setStatus('resolved')
      setTimeout(() => {
        setStatus('idle')
      }, SUCCESS_DURATION)
    }
  }, [addGlobalAttributes, selectVariantOutput.ctaButton, cart.attributes, isValidToAdd, setStatus])

  return {
    ...selectVariantOutput,
    colorGroups: selectVariantOutput.colorGroups ?? [],
    pdpHref,
    product,
    productGroupHandle,
    onProductView,
    quantity,
    incrementQuantity,
    decrementQuantity,
    onQuantityChange,
    showMobileContents,
    toggleMobileContents,
    viewProductText: viewProduct,
    briefProductDescription,
    handleAddToCart,
    status,
    pendingLabel: inFlightAddToBagCta,
    resolvedLabel: successCta,
  }
}
